import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { FolderOpen, Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";
import MediaPickerModal from "./MediaPickerModal";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  board_image_url: "",
  targets: [
    { key: "T1", fa: "", en: "", rect: { x: 0, y: 0, w: 0, h: 0 } },
    { key: "T2", fa: "", en: "", rect: { x: 0, y: 0, w: 0, h: 0 } },
  ],
  items: [
    { key: "I1", fa: "", en: "", targetKey: "T1" },
    { key: "I2", fa: "", en: "", targetKey: "T2" },
  ],
  shuffleItems: true,
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};

  const board_image_url = body?.board?.imageUrl || "";

  const defaultForm = makeEmptyForm();

  const targets =
    Array.isArray(body.targets) && body.targets.length
      ? body.targets.map((t, idx) => ({
          key: t.key || `T${idx + 1}`,
          fa: t.label?.fa || "",
          en: t.label?.en || "",
          rect: t.rect || { x: 0, y: 0, w: 0, h: 0 },
        }))
      : defaultForm.targets;

  const items =
    Array.isArray(body.items) && body.items.length
      ? body.items.map((it, idx) => ({
          key: it.key || `I${idx + 1}`,
          fa: it.label?.fa || "",
          en: it.label?.en || "",
          targetKey: it.targetKey || targets[0]?.key || "T1",
        }))
      : defaultForm.items;

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    board_image_url,
    targets,
    items,
    shuffleItems:
      typeof body.shuffleItems === "boolean" ? body.shuffleItems : true,
    layout: body.layout || "top-bottom",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function DragDropItemTargetModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const imageRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startCoords, setStartCoords] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [fileModalOpen, setFileModalOpen] = useState(false);

  const isImageLoaded =
    form.board_image_url && form.board_image_url.trim().length > 0;

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleMouseDown = (e) => {
    if (!isImageLoaded) return;
    setIsDrawing(true);
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setStartCoords({ x, y });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !isImageLoaded) return;
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newRect = {
      x: Math.min(startCoords.x, x),
      y: Math.min(startCoords.y, y),
      w: Math.abs(startCoords.x - x),
      h: Math.abs(startCoords.y - y),
    };
    setCurrentRect(newRect);
  };

  const handleMouseUp = () => {
    if (!isDrawing || !isImageLoaded || !currentRect) return;
    setIsDrawing(false);

    const targetIndex = form.targets.findIndex(
      (t) => !t.rect || (t.rect.w === 0 && t.rect.h === 0),
    );

    if (targetIndex !== -1) {
      setForm((f) => {
        const targets = [...f.targets];
        targets[targetIndex].rect = currentRect;
        return { ...f, targets };
      });
    } else {
      toast.info(t("dragdrop_it.toasts.no_empty_target"));
    }
    setCurrentRect(null);
  };

  const addTarget = () =>
    setForm((f) => ({
      ...f,
      targets: [
        ...f.targets,
        {
          key: `T${f.targets.length + 1}`,
          fa: "",
          en: "",
          rect: { x: 0, y: 0, w: 0, h: 0 },
        },
      ],
    }));
  const removeTarget = (idx) =>
    setForm((f) => ({ ...f, targets: f.targets.filter((_, i) => i !== idx) }));
  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          key: `I${f.items.length + 1}`,
          fa: "",
          en: "",
          targetKey: f.targets[0]?.key || "T1",
        },
      ],
    }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("dragdrop_it.toasts.stem_required"));
      return;
    }
    if (form.targets.length < 1) {
      toast.error(t("dragdrop_it.toasts.need_target"));
      return;
    }
    if (form.items.length < 1) {
      toast.error(t("dragdrop_it.toasts.need_item"));
      return;
    }

    const payload = {
      kind: "drag_drop_item_target",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        board: isImageLoaded ? { imageUrl: form.board_image_url } : undefined,
        targets: form.targets.map((t) => ({
          key: t.key,
          label: { fa: t.fa || undefined, en: t.en || undefined },
          rect: t.rect && t.rect.w > 0 && t.rect.h > 0 ? t.rect : undefined,
        })),
        items: form.items.map((it) => ({
          key: it.key,
          label: { fa: it.fa || undefined, en: it.en || undefined },
          targetKey: it.targetKey,
        })),
        shuffleItems: !!form.shuffleItems,
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("dragdrop_it.toasts.updated", "آیتم با موفقیت ویرایش شد")
          : t("dragdrop_it.toasts.created"),
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <>
      <MediaPickerModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        token={token}
      />
      <form onSubmit={submit} className="space-y-4">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.stem_fa")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              placeholder={t("dragdrop_it.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
              height={150}
              toolbar="undo redo | bold italic underline | bullist numlist"
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t("dragdrop_it.stem_en_ph")}
              directionality="ltr"
              height={150}
              toolbar="undo redo | bold italic underline | bullist numlist"
            />
          </div>
        </div>

        {/* Layout Selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("dragdrop_it.layout_label")}
          </label>
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {["top-bottom", "bottom-top", "left-right", "right-left"].map(
              (layout) => (
                <div key={layout} className="flex items-center">
                  <input
                    type="radio"
                    id={`layout-${layout}`}
                    name="layout"
                    value={layout}
                    checked={form.layout === layout}
                    onChange={onChange}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <label
                    htmlFor={`layout-${layout}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {t(`dragdrop_it.layout_question_answer_${layout}`)}
                  </label>
                </div>
              ),
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setFileModalOpen(true)}
          className="px-3 py-2 rounded-lg border bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 whitespace-nowrap"
          title={t("media_picker.title", "Browse")}
        >
          <FolderOpen className="w-4 h-4" />
          {t("media_picker.title", "Browse")}
        </button>

        {/* Board image & Visual Target Selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("dragdrop_it.board_image")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("dragdrop_it.board_image_ph")}
            value={form.board_image_url}
            onChange={(e) =>
              setForm((f) => ({ ...f, board_image_url: e.target.value }))
            }
          />
          {isImageLoaded && (
            <div className="relative mt-4 border rounded-lg overflow-hidden">
              <img
                src={form.board_image_url}
                ref={imageRef}
                className="w-full h-auto pointer-events-none"
                alt="Board Preview"
              />
              <div
                className="absolute inset-0 cursor-crosshair"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                {/* Render existing targets */}
                {form.targets.map(
                  (t, idx) =>
                    t.rect && (
                      <div
                        key={idx}
                        className="absolute border-2 border-green-500 bg-green-500/20 flex items-center justify-center text-xs text-white"
                        style={{
                          top: t.rect.y,
                          left: t.rect.x,
                          width: t.rect.w,
                          height: t.rect.h,
                        }}
                      >
                        {t.key}
                      </div>
                    ),
                )}
                {/* Render current drawing rect */}
                {currentRect && (
                  <div
                    className="absolute border-2 border-blue-500 bg-blue-500/20"
                    style={{
                      top: currentRect.y,
                      left: currentRect.x,
                      width: currentRect.w,
                      height: currentRect.h,
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Targets */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium">
              {t("dragdrop_it.targets")}
            </label>
            <button
              type="button"
              className="text-blue-600 text-sm inline-flex items-center gap-1"
              onClick={addTarget}
            >
              <Plus size={16} />
              {t("dragdrop_it.add_target")}
            </button>
          </div>
          <div className="space-y-2">
            {form.targets.map((item, idx) => (
              <div key={item.key} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold">{item.key}</div>
                  {form.targets.length > 1 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removeTarget(idx)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("dragdrop_it.target_fa_ph")}
                    value={item.fa}
                    onChange={(e) => {
                      const targets = [...form.targets];
                      targets[idx].fa = e.target.value;
                      setForm((f) => ({ ...f, targets }));
                    }}
                  />
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("dragdrop_it.target_en_ph")}
                    value={item.en}
                    onChange={(e) => {
                      const targets = [...form.targets];
                      targets[idx].en = e.target.value;
                      setForm((f) => ({ ...f, targets }));
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Items */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium">
              {t("dragdrop_it.items")}
            </label>
            <button
              type="button"
              className="text-blue-600 text-sm inline-flex items-center gap-1"
              onClick={addItem}
            >
              <Plus size={16} />
              {t("dragdrop_it.add_item")}
            </button>
          </div>
          <div className="space-y-2">
            {form.items.map((it, idx) => (
              <div
                key={it.key}
                className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center"
              >
                <div className="text-xs font-semibold">{it.key}</div>
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("dragdrop_it.item_fa_ph")}
                  value={it.fa}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, items }));
                  }}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("dragdrop_it.item_en_ph")}
                  value={it.en}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx].en = e.target.value;
                    setForm((f) => ({ ...f, items }));
                  }}
                />
                <select
                  className="border rounded-lg px-3 py-2"
                  value={it.targetKey}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx].targetKey = e.target.value;
                    setForm((f) => ({ ...f, items }));
                  }}
                >
                  {form.targets.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.key} — {g.fa || g.en || t("dragdrop_it.target_label")}
                    </option>
                  ))}
                </select>
                {form.items.length > 1 && (
                  <button
                    type="button"
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                    onClick={() => removeItem(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.shuffle")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={String(form.shuffleItems)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  shuffleItems: e.target.value === "true",
                }))
              }
            >
              <option value="true">{t("dragdrop_it.shuffle_yes")}</option>
              <option value="false">{t("dragdrop_it.shuffle_no")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) =>
                setForm((f) => ({ ...f, points: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("dragdrop_it.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update") : t("common.create")}
        </button>
      </form>
    </>
  );
}
